import {useCallback, useState} from 'react';
import {Alert, Image, Platform, ScrollView, View} from 'react-native';
import {useFocusEffect} from '@react-navigation/native';
import {Button, IconButton, Menu} from 'react-native-paper';
import {Text, Touchable} from '../../Components';
import {PageContainer} from '../../Containers';
import {ACCESSIBILITY_TEXT_SIZES, useAccessibility, useTheme} from '../../Hooks';
import {RootLandingScreenProps} from '../../Typings/NavigationTypes';
import logoMark from '../../Assets/adaptMhLogo';
import {
  ASSESSMENT_LANGUAGES,
  AssessmentLanguageCode,
  AssessmentSessionSnapshot,
  loadActiveAssessment,
} from '../../Utils/Assessment';
import {getCopy} from '../../Utils/I18n';
import {flushTelemetry, trackEvent} from '../../Utils/Telemetry';
import getStyles from './Landing.styles';

type Props = RootLandingScreenProps;

// One app_open per process, however often Landing regains focus.
let hasTrackedAppOpen = false;

const Landing: React.FC<Props> = ({navigation}) => {
  const theme = useTheme();
  const styles = getStyles(theme);
  const {settings: accessibilitySettings, updateSettings: updateAccessibilitySettings} =
    useAccessibility();
  const [selectedLanguage, setSelectedLanguage] = useState<AssessmentLanguageCode>('en');
  const [activeAssessment, setActiveAssessment] = useState<AssessmentSessionSnapshot | null>(null);
  const [isLanguageMenuVisible, setIsLanguageMenuVisible] = useState(false);
  const [isResumePromptVisible, setIsResumePromptVisible] = useState(false);
  const copy = getCopy(selectedLanguage);
  const currentLanguageCopy = copy.languageNames[selectedLanguage];
  const hasGuidedActiveAssessment =
    activeAssessment?.status === 'active' &&
    activeAssessment.mode === 'guided' &&
    activeAssessment.startedByProvider &&
    activeAssessment.answers.length > 0;
  const textSizeSymbols = {
    standard: 'A',
    large: 'A+',
    extraLarge: 'A++',
  };
  const textSizeLabels = {
    standard: copy.accessibility.textSizeStandard,
    large: copy.accessibility.textSizeLarge,
    extraLarge: copy.accessibility.textSizeExtraLarge,
  };

  useFocusEffect(
    useCallback(() => {
      const loadSession = async () => {
        const session = await loadActiveAssessment();
        setActiveAssessment(session);
        if (session?.language) {
          setSelectedLanguage(session.language);
        }
      };

      loadSession();
      if (!hasTrackedAppOpen) {
        hasTrackedAppOpen = true;
        trackEvent('app_open');
      }
      // Returning to Landing is a quiet moment to drain the sharing queue.
      flushTelemetry().catch(() => undefined);
    }, []),
  );

  const onLanguageSelect = (language: AssessmentLanguageCode) => {
    if (language !== selectedLanguage) {
      trackEvent('language_changed', {language, payload: {from: selectedLanguage}});
    }
    setSelectedLanguage(language);
    setIsLanguageMenuVisible(false);
    setIsResumePromptVisible(false);
  };

  const onTextSizePress = () => {
    setIsLanguageMenuVisible(false);
    setIsResumePromptVisible(false);
    const currentIndex = ACCESSIBILITY_TEXT_SIZES.indexOf(accessibilitySettings.textSize);
    const nextTextSize =
      ACCESSIBILITY_TEXT_SIZES[(currentIndex + 1) % ACCESSIBILITY_TEXT_SIZES.length];
    updateAccessibilitySettings({textSize: nextTextSize});
  };

  const startClinicalScreening = () => {
    setIsResumePromptVisible(false);
    navigation.navigate('PrivacyIntro', {language: selectedLanguage, clinical: true});
  };

  const continueSavedScreening = () => {
    setIsResumePromptVisible(false);
    navigation.navigate('Questions', {mode: 'recommended', resume: true});
  };

  const onScreeningPress = () => {
    if (!hasGuidedActiveAssessment) {
      startClinicalScreening();
      return;
    }

    if (Platform.OS === 'web') {
      setIsLanguageMenuVisible(false);
      setIsResumePromptVisible(true);
      return;
    }

    Alert.alert(copy.clinicianLanding.resumeTitle, copy.clinicianLanding.resumeBody, [
      {
        text: copy.clinicianLanding.startNew,
        onPress: startClinicalScreening,
        style: 'destructive',
      },
      {
        text: copy.clinicianLanding.continue,
        onPress: () => navigation.navigate('Questions', {mode: 'recommended', resume: true}),
      },
      {
        text: copy.clinicianLanding.cancel,
        style: 'cancel',
      },
    ]);
  };

  const renderLanguageAnchor = () => (
    <Touchable
      accessibilityRole="button"
      accessibilityLabel={`${copy.landing.chooseLanguage}: ${currentLanguageCopy.label}`}
      accessibilityHint={copy.accessibility.selectLanguageHint}
      onPress={() => setIsLanguageMenuVisible((visible) => !visible)}
    >
      <View style={[styles.controlChip, styles.languageControl]}>
        <Text weight="bold" style={styles.controlChipText}>
          {selectedLanguage.toUpperCase()}
        </Text>
      </View>
    </Touchable>
  );

  return (
    <PageContainer withSafeArea withPadding style={styles.container}>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.topBar}>
          <View style={styles.quickControls}>
            {Platform.OS === 'web' ? (
              <View style={styles.languageMenuContainer}>
                {renderLanguageAnchor()}
                {isLanguageMenuVisible ? (
                  <View style={styles.languageMenu}>
                    {ASSESSMENT_LANGUAGES.map((language) => {
                      const isSelected = selectedLanguage === language.code;
                      const languageCopy = copy.languageNames[language.code];
                      const title = `${isSelected ? `${copy.accessibility.selected}: ` : ''}${
                        languageCopy.label
                      }`;

                      return (
                        <Touchable
                          key={language.code}
                          accessibilityRole="button"
                          accessibilityLabel={title}
                          onPress={() => onLanguageSelect(language.code)}
                          style={styles.languageMenuItemTouchable}
                        >
                          <View
                            style={[
                              styles.languageMenuItem,
                              isSelected ? styles.languageMenuItemSelected : null,
                            ]}
                          >
                            <Text
                              weight={isSelected ? 'semiBold' : 'regular'}
                              style={styles.menuText}
                            >
                              {title}
                            </Text>
                          </View>
                        </Touchable>
                      );
                    })}
                  </View>
                ) : null}
              </View>
            ) : (
              <Menu
                visible={isLanguageMenuVisible}
                onDismiss={() => setIsLanguageMenuVisible(false)}
                anchor={renderLanguageAnchor()}
              >
                {ASSESSMENT_LANGUAGES.map((language) => {
                  const isSelected = selectedLanguage === language.code;
                  const languageCopy = copy.languageNames[language.code];

                  return (
                    <Menu.Item
                      key={language.code}
                      title={`${isSelected ? `${copy.accessibility.selected}: ` : ''}${
                        languageCopy.label
                      }`}
                      onPress={() => onLanguageSelect(language.code)}
                    />
                  );
                })}
              </Menu>
            )}

            <Touchable
              accessibilityRole="button"
              accessibilityLabel={`${copy.accessibility.textSize}: ${
                textSizeLabels[accessibilitySettings.textSize]
              }`}
              accessibilityHint={copy.accessibility.textSizeHint}
              onPress={onTextSizePress}
            >
              <View style={styles.controlChip}>
                <Text weight="bold" style={styles.controlChipText}>
                  {textSizeSymbols[accessibilitySettings.textSize]}
                </Text>
              </View>
            </Touchable>

            <Touchable
              accessibilityRole="switch"
              accessibilityState={{checked: accessibilitySettings.highContrast}}
              accessibilityLabel={copy.accessibility.highContrast}
              onPress={() => {
                setIsLanguageMenuVisible(false);
                setIsResumePromptVisible(false);
                updateAccessibilitySettings({highContrast: !accessibilitySettings.highContrast});
              }}
            >
              <View
                style={[
                  styles.controlChip,
                  accessibilitySettings.highContrast ? styles.controlChipActive : null,
                ]}
              >
                <Text weight="bold" style={styles.controlChipText}>
                  HC
                </Text>
              </View>
            </Touchable>

            <IconButton
              icon="information-outline"
              mode="outlined"
              size={18}
              accessibilityLabel={copy.landing.learnMore}
              onPress={() => {
                setIsResumePromptVisible(false);
                navigation.navigate('About');
              }}
              style={styles.infoControl}
              iconColor={theme.colors.primary}
            />
          </View>
        </View>

        {isResumePromptVisible ? (
          <View style={styles.resumePromptCard}>
            <Text weight="bold" fontSize={16} style={styles.resumePromptTitle}>
              {copy.clinicianLanding.resumeTitle}
            </Text>
            <Text style={styles.resumePromptBody}>{copy.clinicianLanding.resumeBody}</Text>
            <View style={styles.resumePromptActions}>
              <Button
                mode="text"
                onPress={() => setIsResumePromptVisible(false)}
                textColor={theme.colors.textLight}
                style={styles.resumePromptButton}
              >
                {copy.clinicianLanding.cancel}
              </Button>
              <Button
                mode="outlined"
                onPress={startClinicalScreening}
                textColor={theme.colors.primary}
                style={styles.resumePromptButton}
              >
                {copy.clinicianLanding.startNew}
              </Button>
              <Button
                mode="contained"
                onPress={continueSavedScreening}
                buttonColor={theme.colors.primary}
                textColor={theme.colors.textContrast}
                style={styles.resumePromptButton}
              >
                {copy.clinicianLanding.continue}
              </Button>
            </View>
          </View>
        ) : null}

        <View style={styles.mainContent}>
          <View style={styles.heroPanel}>
            <View style={styles.brandLockup}>
              <Image source={logoMark} style={styles.appLogo} resizeMode="contain" />

              <Text
                fontSize={38}
                lineHeight={45}
                weight="extraBold"
                align="left"
                accessibilityRole="header"
                style={styles.title}
              >
                ADAPT-MH
              </Text>
            </View>

            <Text fontSize={16} align="left" style={styles.subtitle}>
              {copy.clinicianLanding.tagline}
            </Text>

            <View style={styles.credibilityCue}>
              <Text style={styles.credibilityCueText}>{copy.clinicianLanding.credibilityCue}</Text>
            </View>
          </View>

          <View style={styles.actionsGrid}>
            <Button
              mode="contained"
              icon="clipboard-text-outline"
              accessibilityLabel={copy.clinicianLanding.screening}
              onPress={onScreeningPress}
              style={[styles.actionButton, styles.actionButtonLeft]}
              contentStyle={styles.buttonContent}
              buttonColor={theme.colors.primary}
              textColor={theme.colors.textContrast}
            >
              {copy.clinicianLanding.screening}
            </Button>

            <Button
              mode="outlined"
              icon="school-outline"
              accessibilityLabel={copy.clinicianLanding.training}
              onPress={() => navigation.navigate('Training')}
              style={styles.actionButton}
              contentStyle={styles.buttonContent}
              textColor={theme.colors.primary}
            >
              {copy.clinicianLanding.training}
            </Button>

            <Button
              mode="outlined"
              icon="chart-line"
              accessibilityLabel={copy.clinicianLanding.analytics}
              onPress={() => navigation.navigate('Analytics')}
              style={[styles.actionButton, styles.actionButtonLeft]}
              contentStyle={styles.buttonContent}
              textColor={theme.colors.primary}
            >
              {copy.clinicianLanding.analytics}
            </Button>

            <Button
              mode="outlined"
              icon="tune-variant"
              accessibilityLabel={copy.clinicianLanding.customize}
              onPress={() => navigation.navigate('Customize')}
              style={styles.actionButton}
              contentStyle={styles.buttonContent}
              textColor={theme.colors.primary}
            >
              {copy.clinicianLanding.customize}
            </Button>
          </View>
        </View>
      </ScrollView>
    </PageContainer>
  );
};

export default Landing;
